import React, { useState, useEffect, useRef } from 'react';
import HrnbAlert from '../controls/HrnbAlert';
import { profilesApi, usersApi } from '../../api';
import HrnbButton from "../controls/HrnbButton";
import HrnbForm from "../controls/HrnbForm";
import HrnbModal from "../controls/HrnbModal";
import { CreateProfileIcon } from "../../icons";
import HrnbFormControl from "../controls/HrnbFormControl";
import HrnbLabel from "../controls/HrnbLabel";
import HrnbSelect from "../controls/HrnbSelect";

export default function CreateProfilePopup({ show, onHide, onProfileCreated }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [profileType, setProfileType] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [country, setCountry] = useState('');
    const [city, setCity] = useState('');
    const [socialLinks, setSocialLinks] = useState([{ platform: '', url: '' }]);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const displayNameRef = useRef(null);

    // Автофокус на поле "Имя для отображения" при открытии модалки
    useEffect(() => {
        if (show) {
            const timer = setTimeout(() => {
                const input = displayNameRef.current?.querySelector('input');
                if (input) input.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [show]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = await usersApi.current();
                const data = resp && (resp.data ?? resp);
                if (mounted && data) setCurrentUser(data);
            } catch (err) {
                if (mounted) setCurrentUser(null);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        if (!currentUser) {
            setErrorMessage('Пользователь не авторизован');
            return;
        }
        setSaving(true);
        if (!profileType) {
            setErrorMessage('Выберите тип профиля');
            setSaving(false);
            return;
        }
        const userId = currentUser.id ?? currentUser.userId ?? null;

        // Фильтруем социальные ссылки, оставляем только заполненные
        const filteredSocialLinks = socialLinks
            .filter(link => link.platform && link.url)
            .map(link => ({ platform: link.platform, url: link.url }));

        const payload = {
            userId,
            profileType,
            displayName,
            bio: bio || undefined,
            country: country || undefined,
            city: city || undefined,
            socialLinks: filteredSocialLinks.length > 0 ? filteredSocialLinks : undefined,
        };
        try {
            const createdProfile = await profilesApi.create(payload);
            setSuccessMessage('Профиль успешно создан!');
            setTimeout(() => {
                if (onProfileCreated) {
                    onProfileCreated(createdProfile);
                }
                onHide && onHide();
            }, 1500);
        } catch (err) {
            setErrorMessage(err?.response?.data?.message || err?.message || 'Ошибка при создании профиля');
        } finally {
            setSaving(false);
        }
    };

    return (
        <HrnbModal open={show} onClose={onHide} title="Создание профиля">
            <div>
                {successMessage && <HrnbAlert variant="success" onClose={() => setSuccessMessage('')}>{successMessage}</HrnbAlert>}
                {errorMessage && <HrnbAlert variant="danger" onClose={() => setErrorMessage('')}>{errorMessage}</HrnbAlert>}
                <HrnbForm onSubmit={handleSubmit}>
                    <HrnbLabel>ID пользователя:</HrnbLabel>
                    <HrnbFormControl
                        type="text"
                        placeholder="ID пользователя"
                        value={currentUser ? (currentUser.id ?? currentUser.userId ?? '') : ''}
                        disabled
                    />
                    <HrnbLabel>Тип профиля:</HrnbLabel>
                    <HrnbSelect
                        value={profileType}
                        onChange={setProfileType}
                        required
                        options={[
                            {value: "", label: "Выберите тип профиля"},
                            {value: "MC", label: "MC (исполнитель)"},
                            {value: "JUDGE", label: "Судья"},
                            {value: "ORGANIZER", label: "Организатор"},
                            {value: "SPECTATOR", label: "Зритель"},
                        ]}
                    />
                    <HrnbLabel>Имя для отображения:</HrnbLabel>
                    <div ref={displayNameRef}>
                        <HrnbFormControl
                            type="text"
                            placeholder="Имя для отображения"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            required
                        />
                    </div>
                    <HrnbLabel>Биография:</HrnbLabel>
                    <HrnbFormControl
                        as="textarea"
                        row={3}
                        placeholder="Расскажите о себе (макс. 500 символов)..."
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        maxLength={500}
                    />
                    <HrnbLabel>Страна:</HrnbLabel>
                    <HrnbFormControl
                        type="text"
                        placeholder="Россия"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        maxLength={100}
                    />
                    <HrnbLabel>Город:</HrnbLabel>
                    <HrnbFormControl
                        type="text"
                        placeholder="Москва"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        maxLength={100}
                    />

                    <HrnbLabel style={{marginTop: '16px', display: 'block'}}>Социальные ссылки:</HrnbLabel>
                    {socialLinks.map((link, index) => (
                        <div key={index} style={{display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center'}}>
                            <HrnbSelect
                                value={link.platform}
                                onChange={(value) => {
                                    const updated = [...socialLinks];
                                    updated[index].platform = value;
                                    setSocialLinks(updated);
                                }}
                                options={[
                                    {value: '', label: 'Платформа'},
                                    {value: 'VK', label: 'VK'},
                                    {value: 'TELEGRAM', label: 'Telegram'},
                                    {value: 'YOUTUBE', label: 'YouTube'},
                                    {value: 'INSTAGRAM', label: 'Instagram'},
                                    {value: 'OTHER', label: 'Другое'},
                                ]}
                                style={{flex: 1}}
                            />
                            <HrnbFormControl
                                type="text"
                                placeholder="https://..."
                                value={link.url}
                                onChange={(e) => {
                                    const updated = [...socialLinks];
                                    updated[index].url = e.target.value;
                                    setSocialLinks(updated);
                                }}
                                style={{flex: 2}}
                                maxLength={500}
                            />
                            {socialLinks.length > 1 && (
                                <HrnbButton
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                        const updated = socialLinks.filter((_, i) => i !== index);
                                        setSocialLinks(updated);
                                    }}
                                >
                                    ✕
                                </HrnbButton>
                            )}
                        </div>
                    ))}
                    <HrnbButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setSocialLinks([...socialLinks, { platform: '', url: '' }])}
                        style={{marginBottom: '16px'}}
                    >
                        + Добавить ссылку
                    </HrnbButton>

                    <HrnbButton icon={<CreateProfileIcon/>} type="submit" disabled={saving}>
                        {saving ? 'Создание...' : 'Создать профиль'}
                    </HrnbButton>
                </HrnbForm>
            </div>
        </HrnbModal>
    );
}